import React, { useEffect, useState, useRef, useMemo } from 'react';
/**
 * Studio Launcher - Holographic Portal Experience
 * Futuristic studio selection with 3D portals and particles
 */
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Video, Code2, Sparkles, Zap } from 'lucide-react';
import { useTheme } from '../../../core/contexts/ThemeContext';
interface StudioLauncherProps {
  onSelectStudio: (mode: 'video' | 'coding') => void;
}
export function StudioLauncher({
  onSelectStudio
}: StudioLauncherProps) {
  const {
    theme: currentTheme
  } = useTheme();
  const [hoveredStudio, setHoveredStudio] = useState<'video' | 'coding' | null>(null);
  // [VERWIJDERD] mousePosition state is niet meer nodig
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Bepaal de themavariabelen
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';

  // Detecteer mobiel (Muis-tracking verwijderd)
  useEffect(() => {
    const mobileThreshold = 768;
    const checkMobile = () => {
      const mobileCheck = window.innerWidth < mobileThreshold;
      setIsMobile(mobileCheck);
    };
    // [VERWIJDERD] handleMouseMove functie is verwijderd
    checkMobile();
    window.addEventListener('resize', checkMobile);
    // [VERWIJDERD] mousemove listeners zijn verwijderd
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []); // Afhankelijkheid [isMobile] is nu ook verwijderd

  // Studio configuratie
  const studios = useMemo(() => [{
    id: 'video' as const,
    title: 'AI Video Studio',
    subtitle: 'Character-Driven Storytelling',
    icon: Video,
    gradient: 'from-rose-500 via-pink-500 to-fuchsia-600',
    glowColor: 'rgba(244, 114, 182, 0.6)',
    particleColor: '#ec4899'
  }, {
    id: 'coding' as const,
    title: 'VibeCoding Studio',
    subtitle: 'AI-Powered Rapid Prototyping',
    icon: Code2,
    gradient: 'from-emerald-500 via-teal-500 to-cyan-600',
    glowColor: 'rgba(20, 184, 166, 0.6)',
    particleColor: '#14b8a6'
  }], []);

  // Theme-specifieke achtergrondstijl
  const backgroundStyle = useMemo(() => isLightTheme ? 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' : isHighContrast ? '#000000' : isNeonTheme ? 'radial-gradient(circle at 50% 50%, #0a0a0a 0%, #000000 100%)' : 'radial-gradient(circle at 50% 50%, #0f172a 0%, #020617 100%)', [isLightTheme, isHighContrast, isNeonTheme]);

  // Theme-specifieke grid kleur
  const gridColor = useMemo(() => isNeonTheme ? '#00ffff' : isLightTheme ? '#94a3b8' : '#334155', [isLightTheme, isNeonTheme]);
  return <motion.div ref={containerRef} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }}
  // FIX 1: Verwijder overflow-hidden om scrollen mogelijk te maken
  className="h-full relative" style={{
    background: backgroundStyle
  }} data-id="element-795">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-20" data-id="element-796">
        <motion.div animate={{
        backgroundPosition: ['0% 0%', '100% 100%']
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      }} className="w-full h-full" style={{
        backgroundImage: isHighContrast ? 'none' : `linear-gradient(${gridColor} 1px, transparent 1px), 
                 linear-gradient(90deg, ${gridColor} 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} data-id="element-797" />
      </div>
      
      {/* Floating Particles - Reduced on mobile */}
      {!isHighContrast && <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-798">
          {[...Array(isMobile ? 15 : 30)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 rounded-full" style={{
        background: isNeonTheme ? i % 2 === 0 ? '#00ffff' : '#ff00ff' : isLightTheme ? '#94a3b8' : '#64748b',
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`
      }} animate={{
        y: [0, -30, 0],
        opacity: [0, 1, 0],
        scale: [0, 1, 0]
      }} transition={{
        duration: 3 + Math.random() * 2,
        repeat: Infinity,
        delay: Math.random() * 2
      }} data-id="element-799" />)}
        </div>}

      {/* Content */}
      {/* FIX 1: Gebruik min-h-full, verwijder justify-center, voeg padding toe voor header/dock */}
      <div className="relative z-10 min-h-full flex flex-col items-center p-4 pt-20 pb-32 md:p-8" data-id="element-800">
        
        {/* Header (zonder Parallax) */}
        <motion.div initial={{
        y: -50,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.2,
        type: 'spring'
      }} className="text-center mb-8 md:mb-16" style={{
        // [VERWIJDERD] Parallax transformatie
        transform: 'none'
      }} data-id="element-801">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} className="inline-block mb-4 md:mb-6" data-id="element-802">
            <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center relative ${isHighContrast ? 'bg-white' : ''}`} style={{
            background: isHighContrast ? '#ffffff' : isNeonTheme ? 'linear-gradient(135deg, #00ffff, #ff00ff)' : isLightTheme ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            boxShadow: isNeonTheme ? '0 0 40px rgba(0, 255, 255, 0.6), 0 0 80px rgba(255, 0, 255, 0.4)' : isLightTheme ? '0 20px 60px rgba(139, 92, 246, 0.4)' : '0 0 60px rgba(139, 92, 246, 0.5)'
          }} data-id="element-803">
              <Sparkles className={`w-8 h-8 md:w-10 md:h-10 ${isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-804" />
              {!isHighContrast && <motion.div animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 0, 0.5]
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} className="absolute inset-0 rounded-full" style={{
              background: isNeonTheme ? 'linear-gradient(135deg, #00ffff, #ff00ff)' : 'linear-gradient(135deg, #8b5cf6, #ec4899)'
            }} data-id="element-805" />}
            </div>
          </motion.div>
          <motion.h1 animate={{
          textShadow: isNeonTheme ? ['0 0 20px rgba(0, 255, 255, 0.5)', '0 0 40px rgba(255, 0, 255, 0.5)', '0 0 20px rgba(0, 255, 255, 0.5)'] : undefined
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className={`text-3xl md:text-6xl font-bold mb-3 md:mb-4 ${isHighContrast ? 'text-white' : ''}`} style={{
          background: isHighContrast ? '#ffffff' : isNeonTheme ? 'linear-gradient(135deg, #00ffff, #ff00ff)' : isLightTheme ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'linear-gradient(135deg, #a78bfa, #f472b6)',
          WebkitBackgroundClip: isHighContrast ? 'unset' : 'text',
          WebkitTextFillColor: isHighContrast ? '#ffffff' : 'transparent',
          backgroundClip: isHighContrast ? 'unset' : 'text'
        }} data-id="element-806">
            Choose Your Studio
          </motion.h1>
          <p className={`text-sm md:text-xl ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-300' : 'text-slate-400'}`} data-id="element-807">
            Step into the future of creation
          </p>
        </motion.div>
        
        {/* Studio Portals - Met fix voor mobiele grootte */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 max-w-6xl w-full" data-id="element-808">
          {studios.map((studio, index) => {
          const Icon = studio.icon;
          const isHovered = hoveredStudio === studio.id;
          const delay = index * 0.2;
          // [AANGEPAST] studioTransform nu alleen afhankelijk van isHovered (geen mousePosition)
          const studioTransform = isHovered ? 'scale(1.05)' : 'none';
          return <motion.div key={studio.id} initial={{
            opacity: 0,
            scale: 0.8,
            y: 50
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            delay: 0.4 + delay,
            type: 'spring',
            bounce: 0.4
          }}
          // FIX 2: Gebruik max-w-64 (256px) voor kleinere kaarten op mobiel
          className="relative group cursor-pointer touch-manipulation max-w-64 w-[90%] mx-auto md:w-full md:max-w-sm" onHoverStart={() => !isMobile && setHoveredStudio(studio.id)} onHoverEnd={() => !isMobile && setHoveredStudio(null)} onTouchStart={() => setHoveredStudio(studio.id)} onTouchEnd={() => setTimeout(() => setHoveredStudio(null), 300)} onClick={() => onSelectStudio(studio.id)} style={{
            transform: studioTransform,
            transition: 'transform 0.3s ease-out'
          }} data-id="element-809">
                
                {/* Outer Glow Ring */}
                {!isHighContrast && !isMobile && <motion.div animate={{
              rotate: isHovered ? 360 : 0,
              scale: isHovered ? 1.1 : 1
            }} transition={{
              rotate: {
                duration: 3,
                ease: 'linear',
                repeat: Infinity
              },
              scale: {
                duration: 0.3
              }
            }} className="absolute inset-0 rounded-full opacity-50" style={{
              background: `conic-gradient(from 0deg, ${studio.glowColor}, transparent, ${studio.glowColor})`,
              filter: 'blur(20px)'
            }} data-id="element-810" />}
                
                {/* Portal Container */}
                <motion.div animate={{
              // [VERWIJDERD] 3D rotatie gebaseerd op mousePosition
              rotateY: !isMobile && isHovered ? 10 : 0,
              rotateX: !isMobile && isHovered ? -5 : 0
            }} style={{
              transformStyle: 'preserve-3d',
              perspective: '1000px'
            }} className={`relative aspect-square rounded-2xl md:rounded-3xl overflow-hidden ${isHighContrast ? 'border-4 border-white' : ''}`} data-id="element-811">
                  
                  {/* Glass Background */}
                  <div className={`absolute inset-0 backdrop-blur-2xl ${isLightTheme ? 'bg-white/40' : isHighContrast ? 'bg-gray-900' : isNeonTheme ? 'bg-black/60' : 'bg-slate-900/40'}`} style={{
                border: isHighContrast ? 'none' : `1px solid ${isLightTheme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`
              }} data-id="element-812" />
                  
                  {/* Animated Gradient Overlay */}
                  {!isHighContrast && <motion.div animate={{
                opacity: isHovered ? 0.3 : 0.1,
                scale: isHovered ? 1.2 : 1
              }} className={`absolute inset-0 bg-gradient-to-br ${studio.gradient}`} style={{
                mixBlendMode: 'screen'
              }} data-id="element-813" />}
                  
                  {/* Scan Lines - Desktop only */}
                  {!isHighContrast && !isMobile && <motion.div animate={{
                y: isHovered ? ['0%', '100%'] : '0%'
              }} transition={{
                duration: 2,
                repeat: isHovered ? Infinity : 0,
                ease: 'linear'
              }} className="absolute inset-0 pointer-events-none" style={{
                background: `repeating-linear-gradient(
                          0deg,
                          transparent,
                          transparent 2px,
                          ${studio.glowColor} 2px,
                          ${studio.glowColor} 4px
                        )`,
                opacity: 0.1
              }} data-id="element-814" />}
                  
                  {/* Content */}
                  <div className="relative h-full flex flex-col items-center justify-center p-6 md:p-8 z-10" data-id="element-815">
                    
                    {/* Icon Portal - Responsive size */}
                    <motion.div animate={{
                  scale: isHovered ? [1, 1.2, 1] : 1,
                  rotate: isHovered && !isMobile ? 360 : 0
                }} transition={{
                  scale: {
                    duration: 1.5,
                    repeat: isHovered ? Infinity : 0
                  },
                  rotate: {
                    duration: 2,
                    ease: 'linear'
                  }
                }} className="relative mb-4 md:mb-6" data-id="element-816">
                      <div className={`w-24 h-24 md:w-36 md:h-36 rounded-full flex items-center justify-center relative ${isHighContrast ? 'bg-white' : ''}`} style={{
                    background: isHighContrast ? '#ffffff' : `linear-gradient(135deg, ${studio.gradient.split(' ').slice(1).join(' ')})`,
                    boxShadow: isNeonTheme ? `0 0 60px ${studio.glowColor}, inset 0 0 30px ${studio.glowColor}` : `0 0 40px ${studio.glowColor}`
                  }} data-id="element-817">
                        <Icon className={`w-12 h-12 md:w-20 md:h-20 ${isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-818" />
                        
                        {/* Orbiting Particles - Desktop only */}
                        {!isHighContrast && !isMobile && isHovered && [...Array(8)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-full" style={{
                      background: studio.particleColor,
                      left: '50%',
                      top: '50%',
                      transformOrigin: `${50 + Math.cos(i / 8 * Math.PI * 2) * 60}px ${50 + Math.sin(i / 8 * Math.PI * 2) * 60}px`
                    }} animate={{
                      rotate: 360,
                      scale: [0, 1, 0]
                    }} transition={{
                      rotate: {
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear'
                      },
                      scale: {
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 2 / 8
                      }
                    }} data-id="element-819" />)}
                        
                        {/* Pulse Ring */}
                        {!isHighContrast && isHovered && <motion.div initial={{
                      scale: 1,
                      opacity: 0.6
                    }} animate={{
                      scale: 2,
                      opacity: 0
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity
                    }} className="absolute inset-0 rounded-full border-2" style={{
                      borderColor: studio.particleColor
                    }} data-id="element-820" />}
                      </div>
                    </motion.div>
                    
                    {/* Text */}
                    <motion.div animate={{
                  y: isHovered ? -10 : 0
                }} className="text-center" data-id="element-821">
                      <h3 className={`text-xl md:text-4xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-white' : 'text-white'}`} style={{
                    textShadow: isNeonTheme ? `0 0 20px ${studio.glowColor}` : undefined
                  }} data-id="element-822">
                        {studio.title}
                      </h3>
                      <p className={`text-sm md:text-lg ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-300' : isNeonTheme ? 'text-cyan-300' : 'text-slate-400'}`} data-id="element-823">
                        {studio.subtitle}
                      </p>
                      
                      {/* Enter Indicator */}
                      <motion.div animate={{
                    opacity: isHovered ? 1 : 0,
                    y: isHovered ? 0 : 10
                  }} className="mt-4 md:mt-6 flex items-center justify-center gap-2" data-id="element-824">
                        <Zap className={`w-4 h-4 ${isHighContrast ? 'text-white' : isNeonTheme ? 'text-cyan-400' : 'text-white'}`} data-id="element-825" />
                        <span className={`text-xs md:text-sm font-medium ${isHighContrast ? 'text-white' : isNeonTheme ? 'text-cyan-400' : 'text-white'}`} data-id="element-826">
                          {isMobile ? 'Tap' : 'Click'} to Enter
                        </span>
                      </motion.div>
                    </motion.div>
                  </div>
                  
                  {/* Corner Accents */}
                  {!isHighContrast && <>
                      {['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0', 'bottom-0 right-0'].map((position, i) => <motion.div key={i} animate={{
                  opacity: isHovered ? 1 : 0
                }} className={`absolute ${position} w-6 h-6 md:w-8 md:h-8`} style={{
                  background: `linear-gradient(${i % 2 === 0 ? '135deg' : '45deg'}, ${studio.particleColor}, transparent)`
                }} data-id="element-827" />)}
                    </>}
                </motion.div>
              </motion.div>;
        })}
        </div>
        
        {/* Bottom Hint */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className={`mt-8 md:mt-12 text-center ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-400' : isNeonTheme ? 'text-cyan-400/60' : 'text-slate-500'}`} data-id="element-828">
          <motion.div animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="text-xs md:text-sm" data-id="element-829">
            ✨ {isMobile ? 'Tap' : 'Hover'} to activate •{' '}
            {isMobile ? 'Tap' : 'Click'} to enter ✨
          </motion.div>
        </motion.div>
      </div>
    </motion.div>;
}